import { resolveCurrencyCode } from '../../../utils/currencyUtils';
import {
  InTransactionFormData, OtherAmountRow, parseDepositAccount, convertToInvoiceCurrency,
} from './inTransactionTypes';

/**
 * Rates are mandatory whenever a currency conversion is involved — for the
 * payment itself and for the deduction. Returns an error message or null.
 */
export function validateInTransactionRates(
  data: InTransactionFormData,
  paymentCode: string,
  invoiceCode: string,
  deductionCode: string
): string | null {
  const rateNeeded = paymentCode !== 'EGP' || (!!invoiceCode && invoiceCode !== 'EGP');
  if (rateNeeded && (!data.exchangeRate || data.exchangeRate <= 0)) {
    return paymentCode !== 'EGP'
      ? `Exchange rate (${paymentCode} to EGP) is required.`
      : `Exchange rate (${invoiceCode} to EGP) is required — the EGP amount will be divided by it to apply the payment to the ${invoiceCode} invoice.`;
  }
  const deductionMismatch = (data.deductionAmount || 0) > 0 &&
    !!invoiceCode && !!deductionCode && deductionCode !== invoiceCode;
  if (deductionMismatch && (!data.deductionRate || data.deductionRate <= 0)) {
    const rateCurrency = deductionCode !== 'EGP' ? deductionCode : invoiceCode;
    return `Deduction exchange rate (${rateCurrency} to EGP) is required — the deduction currency differs from the invoice currency.`;
  }
  return null;
}

/**
 * Build the API payload for creating an "in" transaction.
 * Relation fields use *Id keys which the admin resource layer converts
 * to Strapi relation names (invoiceId -> invoice, bankId -> bank, safeId -> safe).
 */
export function buildInTransactionPayload(
  data: InTransactionFormData,
  otherAmounts: OtherAmountRow[],
  currencies: any[] | null | undefined,
  selectedInvoice: any | null
): any {
  const invoiceRef = selectedInvoice
    ? ` for invoice ${selectedInvoice.invoiceNumber || `#${selectedInvoice.id}`}`
    : '';
  const payload: any = {
    type: 'in',
    description: `Incoming payment${invoiceRef}`,
    amount: data.amount,
    currency: data.currency,
    date: data.transactionDate || new Date().toISOString().split('T')[0],
  };

  // Invoices live in the invoice-hire collection, so the transaction links
  // through the invoiceHire relation (invoiceHireId -> invoiceHire).
  if (data.invoiceId) {
    payload.invoiceHireId = data.invoiceId;
  }

  const resolvedCode = resolveCurrencyCode(data.currency, currencies as any[]);
  if (data.exchangeRate) {
    payload.exchangeRate = data.exchangeRate;
  }

  // Amount converted into the invoice's currency (what the payment is worth
  // against the invoice). EGP paid on a USD invoice: amount ÷ rate.
  // USD paid on an EGP invoice: amount × rate. Same currency: amount as-is.
  if (data.invoiceId && selectedInvoice) {
    const invoiceCode = resolveCurrencyCode(selectedInvoice.currency, currencies as any[]);
    const applied = convertToInvoiceCurrency(data.amount, resolvedCode, invoiceCode, data.exchangeRate);
    payload.invoiceAppliedAmount = Math.round((applied || data.amount) * 100) / 100;
  }

  if (data.amountCollectedCurrency && data.amountCollectedCurrency !== data.currency) {
    payload.amountCollectedCurrency = data.amountCollectedCurrency;
    if (data.amountCollectedRate) {
      payload.amountCollectedRate = data.amountCollectedRate;
    }
  }

  // Cheque / bank details
  if (data.chequeDate) payload.chequeDate = data.chequeDate;
  if (data.chequeNumber) payload.chequeNumber = data.chequeNumber;
  if (data.bankName) payload.bankName = data.bankName;

  // Money-in destination account (bank or safe)
  const account = parseDepositAccount(data.depositAccount);
  if (account?.type === 'bank') payload.bankId = account.id;
  if (account?.type === 'safe') payload.safeId = account.id;

  // Invoice deduction — converted into the invoice's currency like the payment
  if (data.invoiceId && data.deductionAmount && data.deductionAmount > 0) {
    payload.deductionAmount = data.deductionAmount;
    if (data.deductionCurrency) {
      payload.deductionCurrency = data.deductionCurrency;
    }
    if (data.deductionRate) {
      payload.deductionRate = data.deductionRate;
    }
    if (selectedInvoice) {
      const invoiceCode = resolveCurrencyCode(selectedInvoice.currency, currencies as any[]);
      const deductionCode = resolveCurrencyCode(data.deductionCurrency || data.currency, currencies as any[]);
      const appliedDeduction = convertToInvoiceCurrency(
        data.deductionAmount, deductionCode, invoiceCode, data.deductionRate);
      payload.deductionAppliedAmount = Math.round((appliedDeduction || data.deductionAmount) * 100) / 100;
    }
  }

  if (data.notes) payload.notes = data.notes;

  if (otherAmounts.length > 0) {
    payload.otherAmounts = otherAmounts
      .filter(oa => oa.amount > 0)
      .map(oa => {
        const rowAccount = parseDepositAccount(oa.depositAccount);
        return {
          amount: oa.amount,
          currency: oa.currency,
          exchangeRate: oa.exchangeRate || undefined,
          chequeDate: oa.chequeDate || undefined,
          chequeNumber: oa.chequeNumber || undefined,
          bankName: oa.bankName || undefined,
          accountType: rowAccount?.type,
          accountId: rowAccount?.id,
        };
      });
  }

  return payload;
}
